// Teams beyond switching (shared/API.md §41.6, docs/PARITY.md P017 / P232 / P233 / P271). PURE.
//
// - Joinable teams: `GET /teams` lists what the reader may see; the ones not already joined, not
//   deleted, and open (`type: "O"` or `allow_open_invite`) get «Unirse». Sorted by name, Spanish
//   collation (accents and case folded), like the rest of the web's people/channel lists.
// - After leaving a team the workspace lands on the first remaining team in the strip's order.
// - ⌘1–⌘9 (Mac `macSelectTeamIndex`): the digit picks the strip's team at that position; an index
//   past the list is ignored. On the web the browser keeps ⌘/Ctrl+1–9 for its own tabs (Chrome does
//   not hand them to the page outside an installed app window), so ⌥ is accepted as well:
//   ⌘⌥1–9 / Ctrl+Alt+1–9 always reach the app.

export interface TeamListing {
  id: string;
  display_name: string;
  name: string;
  description?: string;
  type?: string;
  allow_open_invite?: boolean;
  delete_at?: number;
  last_team_icon_update?: number;
}

const collator = new Intl.Collator('es', { sensitivity: 'base' });

export function joinableTeams(all: readonly TeamListing[], mine: ReadonlySet<string>): TeamListing[] {
  return all
    .filter((t) => !mine.has(t.id) && !(t.delete_at ?? 0) && (t.type === 'O' || t.allow_open_invite === true))
    .sort((a, b) => collator.compare(a.display_name || a.name, b.display_name || b.name) || (a.id < b.id ? -1 : 1));
}

/** The team to land on after leaving `leavingId`: the first other team in strip order, or null. */
export function teamAfterLeaving(stripOrder: readonly string[], leavingId: string): string | null {
  return stripOrder.find((id) => id !== leavingId) ?? null;
}

export interface ShortcutKey {
  key: string;
  code?: string;
  metaKey: boolean;
  ctrlKey: boolean;
  altKey: boolean;
  shiftKey: boolean;
}

/**
 * The 0-based team index a key press asks for, or null. ⌘ on a Mac, Ctrl elsewhere, with or without
 * ⌥/Alt; Shift never (⌘⇧ digits are other shortcuts). Uses `code` so ⌥+digit (which types a symbol
 * on a Mac keyboard) still reads as the digit.
 */
export function teamShortcutIndex(e: ShortcutKey, isMac: boolean): number | null {
  if (e.shiftKey) return null;
  const primary = isMac ? e.metaKey && !e.ctrlKey : e.ctrlKey && !e.metaKey;
  if (!primary) return null;
  const fromCode = e.code && /^(Digit|Numpad)([1-9])$/.exec(e.code);
  const digit = fromCode ? fromCode[2] : /^[1-9]$/.test(e.key) ? e.key : null;
  return digit ? Number(digit) - 1 : null;
}

/** Folded accent-insensitive matching on a person's name or username (team members list). */
export function matchesPerson(query: string, fields: ReadonlyArray<string | null | undefined>): boolean {
  const needle = fold(query.trim().replace(/^@/, ''));
  if (!needle) return true;
  return fields.some((f) => !!f && fold(f).includes(needle));
}

function fold(text: string): string {
  return text.normalize('NFD').replace(/\p{Mn}/gu, '').toLowerCase();
}

export const TeamDirectory = { joinableTeams, teamAfterLeaving, teamShortcutIndex, matchesPerson } as const;
